import { useEffect, useState } from "react";
import type { Image } from "../../types/common/image";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getPage, updatePage, clearPage } from "../../redux/slices/pageSlice";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import RichTextEditor from "../common/ckeditor/RichTextEditor";
import buttonLoadingAnimation from "../../animations/buttonLoading.json";
import Lottie from "lottie-react";

type Props = {
  onBack: () => void;
  selectedId: string;
};

export const UpdatePage = ({ onBack, selectedId }: Props) => {
  const dispatch = useAppDispatch();
  const { page: currentPage } = useAppSelector((state) => state.page);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [images, setImages] = useState<Image[]>([]);
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDesc, setSeoDesc] = useState("");
  const [seoKeywords, setSeoKeywords] = useState("");

  useEffect(() => {
    dispatch(getPage(selectedId));
    return () => {
      dispatch(clearPage());
    };
  }, [dispatch, selectedId]);

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (currentPage && !initialized) {
      setTitle(currentPage.title);
      setContent(currentPage.content);
      setImages(currentPage.images || []);
      setSeoTitle(currentPage.seo?.title || "");
      setSeoDesc(currentPage.seo?.description || "");
      setSeoKeywords(currentPage.seo?.keywords?.join(", ") || "");
      setInitialized(true);
    }
  }, [currentPage, initialized]);

  const handleEditorImageUpload = (image: Image) => {
    setImages((prev) => [...prev, image]);
  };

  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Vui lòng nhập tiêu đề");
      return;
    }
    if (!content.trim()) {
      toast.error("Vui lòng nhập nội dung");
      return;
    }

    setLoading(true);
    try {
      const payload: Parameters<typeof updatePage>[0] = {
        id: selectedId,
        data: {
          title,
          content,
          images,
        },
      };
      if (seoTitle || seoDesc || seoKeywords) {
        payload.data.seo = {
          title: seoTitle,
          description: seoDesc,
          keywords: seoKeywords
            .split(",")
            .map((k) => k.trim())
            .filter(Boolean),
        };
      }
      await dispatch(updatePage(payload)).unwrap();
      toast.success("Cập nhật trang thành công");
      onBack();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (!currentPage) {
    return (
      <div className="p-5">
        <p className="text-sm text-gray-500 text-center">Đang tải trang...</p>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Chỉnh sửa trang</h1>
        <button
          type="button"
          onClick={onBack}
          className="p-2 text-sm bg-gray-500 rounded-lg text-white cursor-pointer hover:bg-gray-600 transition-colors duration-300"
        >
          Quay lại
        </button>
      </div>
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl border border-gray-100 p-5 space-y-4"
      >
        {/* Title */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Tiêu đề <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 placeholder:text-sm placeholder:text-gray-400"
            placeholder="Nhập tiêu đề trang"
          />
        </div>

        {/* Content (CKEditor) */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Nội dung <span className="text-red-500">*</span>
          </label>
          {initialized && (
            <RichTextEditor
              value={content}
              onChange={setContent}
              onUpload={handleEditorImageUpload}
            />
          )}
        </div>

        {/* SEO Section */}
        <div className="border-t border-gray-200 pt-4 space-y-3">
          <p className="text-sm font-semibold text-gray-700">
            SEO (Tùy chọn)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-xs text-gray-500">SEO Title</label>
              <input
                type="text"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 text-sm placeholder:text-gray-400"
                placeholder="Tiêu đề SEO"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-xs text-gray-500">Keywords</label>
              <input
                type="text"
                value={seoKeywords}
                onChange={(e) => setSeoKeywords(e.target.value)}
                className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 text-sm placeholder:text-gray-400"
                placeholder="từ khóa 1, từ khóa 2, ..."
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="block text-xs text-gray-500">
              SEO Description
            </label>
            <textarea
              value={seoDesc}
              onChange={(e) => setSeoDesc(e.target.value)}
              rows={2}
              className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 text-sm placeholder:text-gray-400 resize-none"
              placeholder="Mô tả SEO"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end pt-2">
          {loading ? (
            <Lottie
              animationData={buttonLoadingAnimation}
              loop={true}
              className="h-10 w-20"
            />
          ) : (
            <button
              type="submit"
              className="p-2 px-6 text-sm text-white bg-green-500 rounded-lg cursor-pointer hover:bg-green-600 transition-colors duration-300"
            >
              Cập nhật
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
